// Canonical vocabulary for dossier / application status.
// Single source of truth: every status id maps to a Dutch display label, a
// badge variant, and (where meaningful) the journey stage it corresponds to
// in process-stepper.tsx. Pages consume `StatusBadge` instead of hand-rolling
// `<span className={`badge badge--${variant}`}>{label}</span>`.

import { cn } from "@/lib/utils";

import type { ProcessStageId } from "./process-stepper";

export type BadgeVariant = "success" | "warning" | "danger" | "info";

export type DossierStatusId =
  | "in-behandeling"
  | "issues-gevonden"
  | "secr-klaar"
  | "secr-klaar-check"
  | "bureau-review"
  | "advies-verzonden"
  | "goedgekeurd"
  | "herziening-nodig"
  | "revisie-gevraagd"
  | "aanpassingen-akkoord";

export interface DossierStatus {
  id: DossierStatusId;
  /** Dutch label shown in the badge. */
  label: string;
  /** Badge colour variant (maps to `.badge--*` in ua.css). */
  variant: BadgeVariant;
  /** Where this status sits in the end-to-end journey, when meaningful. */
  stage?: ProcessStageId;
}

export const DOSSIER_STATUSES: Record<DossierStatusId, DossierStatus> = {
  "in-behandeling": {
    id: "in-behandeling",
    label: "In behandeling",
    variant: "info",
    stage: "bureau",
  },
  "issues-gevonden": {
    id: "issues-gevonden",
    label: "Issues gevonden",
    variant: "warning",
    stage: "prescreening",
  },
  "secr-klaar": {
    id: "secr-klaar",
    label: "Secr. klaar",
    variant: "success",
    stage: "klaar-check",
  },
  "secr-klaar-check": {
    id: "secr-klaar-check",
    label: "Secr. klaar-check",
    variant: "info",
    stage: "klaar-check",
  },
  "bureau-review": {
    id: "bureau-review",
    label: "Bureau review",
    variant: "info",
    stage: "bureau",
  },
  "advies-verzonden": {
    id: "advies-verzonden",
    label: "Advies verzonden",
    variant: "success",
    stage: "advies",
  },
  goedgekeurd: { id: "goedgekeurd", label: "Goedgekeurd", variant: "success", stage: "afgerond" },
  "herziening-nodig": {
    id: "herziening-nodig",
    label: "Herziening nodig",
    variant: "danger",
    stage: "intake",
  },
  "revisie-gevraagd": {
    id: "revisie-gevraagd",
    label: "Feedback verwerken",
    variant: "warning",
    stage: "afgerond",
  },
  "aanpassingen-akkoord": {
    id: "aanpassingen-akkoord",
    label: "Aanvaard",
    variant: "success",
    stage: "afgerond",
  },
};

export function statusById(id: DossierStatusId): DossierStatus {
  return DOSSIER_STATUSES[id];
}

interface StatusBadgeProps {
  status: DossierStatusId;
  /** Extra classes merged onto the badge (e.g. spacing/size tweaks). */
  className?: string;
}

export function StatusBadge({ status, className }: StatusBadgeProps) {
  const { label, variant } = statusById(status);
  return <span className={cn(`badge badge--${variant}`, className)}>{label}</span>;
}
